import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChartBar, Scissors, Spinner, Archive, FileXls,
  CurrencyDollar, Users, Package, Warning, X,
} from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { downloadWithAuth } from '@/lib/apiClient';
import { useNarrow, MobileRows, MobileRow, KV } from '@/components/historico/MobileRows';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBRL = (c) => ((c || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

const TABS = [
  { id: 'entradas', label: 'Entradas por caixa', icon: CurrencyDollar },
  { id: 'servicos', label: 'Serviços realizados', icon: ChartBar },
  { id: 'produtos', label: 'Produtos vendidos', icon: Package },
  { id: 'clientes', label: 'Clientes atendidos', icon: Users },
];

const ProducaoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toUpperCase() === 'ADMIN';
  const [tab, setTab] = useState('entradas');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [temHistorico, setTemHistorico] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [resumo, setResumo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/historico/producao/periodo-padrao`);
        if (!r.ok) return;
        const d = await r.json();
        setTemHistorico(!!d.tem_dados);
        if (d.tem_dados) { setInicio(d.data_inicio); setFim(d.data_fim); }
      } finally { setBootLoading(false); }
    })();
  }, []);

  // Resumo cruzado — recarrega quando periodo muda
  useEffect(() => {
    if (!inicio || !fim) { setResumo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/historico/producao/resumo?inicio=${inicio}&fim=${fim}`);
        const d = r.ok ? await r.json() : null;
        if (!cancelled && d) setResumo(d);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [inicio, fim]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#F7F6F4] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E4E4E7] rounded-2xl p-8 max-w-md text-center">
          <Warning size={32} className="mx-auto text-[#DC2626] mb-3" />
          <h2 className="text-lg font-semibold text-[#18181B] mb-2">Acesso restrito</h2>
          <p className="text-sm text-[#71717A] mb-4">Esta área é exclusiva para administradores.</p>
          <button onClick={() => navigate('/gestao')} className="salonup-btn-back" data-testid="btn-voltar-gestao">
            <ArrowLeft size={16} /> Voltar
          </button>
        </div>
      </div>
    );
  }

  if (bootLoading) {
    return <div className="min-h-screen bg-[#F7F6F4] flex items-center justify-center"><Spinner size={28} className="animate-spin text-[#71717A]" /></div>;
  }

  if (temHistorico === false) {
    return (
      <div className="min-h-screen bg-[#F7F6F4]">
        <div className="salonup-container">
          <Header navigate={navigate} />
          <div className="salonup-card p-10 text-center max-w-md mx-auto" data-testid="producao-vazio">
            <Archive size={40} className="mx-auto text-[#A1A1AA] mb-3" />
            <h2 className="text-lg font-semibold mb-2">Ainda não há lançamentos</h2>
            <p className="text-sm text-[#71717A]">
              Nenhum histórico foi importado até agora. Importe um lote no card <b>Histórico</b> para começar.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const diferenca = resumo ? resumo.diferenca_centavos : 0;
  const hasDif = resumo && diferenca !== 0;

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="producao-page">
      <div className="salonup-container">
        <Header navigate={navigate} />

        <section className="mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="salonup-heading-lg">Produção</h1>
            <span className="salonup-badge" style={{ background: '#F3E8FF', color: '#6B21A8' }}>Histórico importado</span>
          </div>
          <p className="salonup-text-body">Leitura da execução do salão por período. Somente leitura.</p>
        </section>

        {/* Filtro de periodo */}
        <div className="bg-white rounded-2xl border border-[#E4E4E7] p-4 mb-5 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-center">
          <div className="min-w-0">
            <label className="text-[10px] uppercase text-[#71717A] tracking-wide block mb-1">De</label>
            <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} data-testid="input-inicio" className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" />
          </div>
          <div className="min-w-0">
            <label className="text-[10px] uppercase text-[#71717A] tracking-wide block mb-1">Até</label>
            <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} data-testid="input-fim" className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" />
          </div>
        </div>

        {resumo && <KpiRow resumo={resumo} />}
        <RecebimentosPorForma inicio={inicio} fim={fim} />

        {hasDif && (
          <div className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-xs text-amber-900 flex items-start gap-2" data-testid="aviso-diferenca">
            <Warning size={14} className="mt-0.5" />
            <span>
              Aviso: Entradas (aba 1) difere de Serviços + Produtos (abas 2 e 3) em
              <b> {fmtBRL(Math.abs(diferenca))}</b>. Isso ocorre quando a origem traz pagamentos de comandas que estão fora do período filtrado por data da comanda.
            </span>
          </div>
        )}

        <div className="flex gap-2 mb-4 flex-wrap">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${tab === t.id ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'bg-white text-[#71717A] border-[#E4E4E7]'}`}>
                <Icon size={14} className="inline mr-1" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'entradas' && <AbaEntradas inicio={inicio} fim={fim} />}
        {tab === 'servicos' && <AbaServicos inicio={inicio} fim={fim} />}
        {tab === 'produtos' && <AbaProdutos inicio={inicio} fim={fim} />}
        {tab === 'clientes' && <AbaClientes inicio={inicio} fim={fim} />}
      </div>
    </div>
  );
};

const Header = ({ navigate }) => (
  <header className="salonup-header">
    <div className="salonup-brand">
      <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
      <span className="salonup-logo-text">Salon<span>Up</span></span>
    </div>
    <button onClick={() => navigate('/gestao')} className="salonup-btn-back" data-testid="btn-voltar">
      <ArrowLeft size={18} weight="bold" /> <span>Voltar</span>
    </button>
  </header>
);

// KPIs do topo: lista configuravel (um 4o item, ex. "Saidas", entra so adicionando aqui)
const KPIS = [
  { key: 'total_pagamentos_centavos', label: 'Entradas (aba 1)', testId: 'kpi-entradas' },
  { key: 'total_servicos_centavos', label: 'Serviços (aba 2)', testId: 'kpi-servicos' },
  { key: 'total_produtos_centavos', label: 'Produtos (aba 3)', testId: 'kpi-produtos' },
];

const KpiRow = ({ resumo }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="kpi-row">
    {KPIS.map(k => (
      <div key={k.key} className="bg-white rounded-2xl border border-[#E4E4E7] px-4 py-3" data-testid={k.testId}>
        <div className="uppercase tracking-wide text-[10px] text-[#71717A]">{k.label}</div>
        <div className="text-[#18181B] font-semibold text-lg">{fmtBRL(resumo[k.key])}</div>
      </div>
    ))}
  </div>
);

const FAMILIA_COR = { 'PIX': '#0F766E', 'DINHEIRO': '#15803D', 'CARTÃO': '#1D4ED8', 'OUTRAS': '#71717A' };
const fmtPct = (v) => `${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;
const curtoForma = (f) => f.replace(/^Cart[aã]o\s*[–-]\s*/i, '');

const FamiliaCard = ({ f, aberta, onToggle }) => (
  <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden" data-testid={`familia-${f.familia}`}>
    <button onClick={onToggle} className="w-full text-left px-4 py-3 hover:bg-[#F7F6F4] transition-colors" data-testid={`familia-toggle-${f.familia}`}>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: FAMILIA_COR[f.familia] || '#71717A' }} />
        <span className="uppercase tracking-wide text-[10px] text-[#71717A]">{f.familia}</span>
        <span className="ml-auto text-[10px] text-[#A1A1AA]">{aberta ? 'fechar' : 'abrir'}</span>
      </div>
      <div className="text-[#18181B] font-semibold text-lg" data-testid={`familia-valor-${f.familia}`}>{fmtBRL(f.valor_centavos)}</div>
      <div className="text-xs text-[#71717A]">{f.qtd} pagamentos · {fmtPct(f.pct)} das entradas</div>
    </button>
    {aberta && (
      <div className="border-t border-[#F4F4F5] px-4 py-2">
        {f.formas.length === 0 && <div className="text-xs text-[#A1A1AA]">Sem pagamentos no período.</div>}
        {f.formas.map(x => (
          <div key={x.forma_pagamento} className="flex text-xs py-1" data-testid={`forma-${x.forma_pagamento}`}>
            <span className="text-[#18181B] min-w-0 flex-1 break-words">{x.forma_pagamento}</span>
            <span className="ml-auto shrink-0 pl-2 text-[#71717A] whitespace-nowrap">{x.qtd} · {fmtPct(x.pct)}</span>
            <span className="w-28 shrink-0 text-right font-medium whitespace-nowrap">{fmtBRL(x.valor_centavos)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const BandeirasCards = ({ d }) => (
  <div className="space-y-2">
    {d.bandeiras.map(b => (
      <MobileRow key={b.bandeira} testId={`bandeira-${b.bandeira}`} title={b.bandeira} value={fmtBRL(b.valor_centavos)} meta={[['Pagamentos', b.qtd]]}>
        <div className="mt-1 border-t border-[#F4F4F5] pt-1">
          <KV label="Crédito" value={fmtBRL(b.credito_centavos)} />
          <KV label="Débito" value={fmtBRL(b.debito_centavos)} />
          {d.colunas_outras.map(o => <KV key={o} label={curtoForma(o)} value={fmtBRL(b.outras[o] || 0)} />)}
        </div>
      </MobileRow>
    ))}
    <div className="px-3 py-2 rounded-xl border-2 border-[#E4E4E7] bg-white" data-testid="bandeiras-total">
      <KV strong label={`Total CARTÃO · ${d.bandeiras.reduce((a, b) => a + b.qtd, 0)} pagamentos`} value={fmtBRL(d.cartao_total_centavos)} />
    </div>
  </div>
);

const BandeirasTable = ({ d }) => {
  const narrow = useNarrow();
  return (
  <div className="bg-white rounded-2xl border border-[#E4E4E7] p-4" data-testid="bandeiras-table">
    <div className="text-xs font-semibold text-[#71717A] uppercase mb-2">Bandeiras (CARTÃO)</div>
    {narrow ? <BandeirasCards d={d} /> : (
    <table className="w-full text-xs">
      <thead className="text-[#A1A1AA] text-[10px] uppercase"><tr>
        <th className="text-left py-1">Bandeira</th><th className="text-right">Pagamentos</th><th className="text-right">Total</th>
        <th className="text-right">Crédito</th><th className="text-right">Débito</th>
        {d.colunas_outras.map(o => <th key={o} className="text-right" title={o}>{curtoForma(o)}</th>)}
      </tr></thead>
      <tbody>
        {d.bandeiras.map(b => (
          <tr key={b.bandeira} className="border-t border-[#F4F4F5]" data-testid={`bandeira-${b.bandeira}`}>
            <td className="py-1.5">{b.bandeira}</td><td className="text-right">{b.qtd}</td>
            <td className="text-right font-medium">{fmtBRL(b.valor_centavos)}</td>
            <td className="text-right">{fmtBRL(b.credito_centavos)}</td><td className="text-right">{fmtBRL(b.debito_centavos)}</td>
            {d.colunas_outras.map(o => <td key={o} className="text-right">{fmtBRL(b.outras[o] || 0)}</td>)}
          </tr>
        ))}
        <tr className="border-t-2 border-[#E4E4E7] font-semibold" data-testid="bandeiras-total">
          <td className="py-1.5">Total CARTÃO</td><td className="text-right">{d.bandeiras.reduce((a, b) => a + b.qtd, 0)}</td>
          <td className="text-right">{fmtBRL(d.cartao_total_centavos)}</td><td colSpan={2 + d.colunas_outras.length} />
        </tr>
      </tbody>
    </table>
    )}
  </div>
  );
};

const RecebimentosPorForma = ({ inicio, fim }) => {
  const { data: d } = useReport(inicio && fim ? `${API}/historico/producao/formas?inicio=${inicio}&fim=${fim}` : '', inicio, fim);
  const [aberta, setAberta] = useState(null);
  if (!d) return null;
  const c = d.checagens;
  return (
    <section className="mb-5" data-testid="recebimentos-por-forma">
      <div className="text-xs font-semibold text-[#71717A] uppercase mb-2">Recebimentos por forma</div>
      {(!c.familias_ok || !c.bandeiras_ok) && (
        <div className="mb-3 p-3 rounded-xl border border-red-200 bg-red-50 text-xs text-red-800" data-testid="aviso-formas">
          <Warning size={14} className="inline mr-1" />
          {!c.familias_ok && <>Soma das famílias difere das Entradas em <b>{fmtBRL(c.diferenca_familias_centavos)}</b>. </>}
          {!c.bandeiras_ok && <>Soma das bandeiras difere do total de CARTÃO em <b>{fmtBRL(c.diferenca_bandeiras_centavos)}</b>.</>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-3 items-start">
        {d.familias.map(f => <FamiliaCard key={f.familia} f={f} aberta={aberta === f.familia} onToggle={() => setAberta(aberta === f.familia ? null : f.familia)} />)}
      </div>
      {d.vendas_online && (
        <div className="mb-3 px-4 py-2 rounded-xl bg-white border border-[#E4E4E7] text-xs text-[#52525B] flex flex-wrap gap-x-3" data-testid="vendas-online">
          <span className="uppercase tracking-wide text-[10px] text-[#71717A]">Vendas online (Pix online + Cartão online)</span>
          <span className="ml-auto font-semibold text-[#18181B] whitespace-nowrap">{fmtBRL(d.vendas_online.valor_centavos)} · {d.vendas_online.qtd} pagamentos</span>
        </div>
      )}
      <BandeirasTable d={d} />
    </section>
  );
};

const useReport = (url, inicio, fim) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!inicio || !fim || !url) return;
    let cancelled = false;
    setLoading(true);
    fetch(url).then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setData(d); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [url, inicio, fim]);
  return { data, loading };
};

const ExportBtn = ({ url, filename, testId }) => (
  <button onClick={() => downloadWithAuth(url, filename)} data-testid={testId}
    className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white">
    <FileXls size={14} weight="bold" /> Exportar Excel
  </button>
);

// ======================= Aba 1 =======================
const AbaEntradas = ({ inicio, fim }) => {
  const [forma, setForma] = useState('');
  const url = inicio && fim ? `${API}/historico/producao/entradas?inicio=${inicio}&fim=${fim}${forma ? `&forma_pagamento=${encodeURIComponent(forma)}` : ''}` : '';
  const expUrl = url.replace('/entradas?', '/entradas/export/excel?');
  const { data, loading } = useReport(url, inicio, fim);
  const formasList = useMemo(() => (data?.por_forma_pagamento || []).map(f => f.forma_pagamento), [data]);
  const narrow = useNarrow();

  return (
    <Card title="Entradas por caixa" exportUrl={expUrl} filename={`entradas_${inicio}_a_${fim}.xlsx`} testId="aba-entradas">
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <select value={forma} onChange={(e) => setForma(e.target.value)} data-testid="filtro-fp" className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm">
          <option value="">Todas as formas</option>
          {formasList.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
      {loading && <Loading />}
      {!loading && data && (
        <>
          {narrow ? (
            <MobileRows testPrefix="row-entrada" empty="Sem linhas neste período." rows={data.linhas.map(p => ({
              title: p.cliente_origem || `Comanda ${p.comanda}`, value: fmtBRL(p.valor_centavos),
              meta: [['', fmtDate(p.data_comanda)], ['Comanda', p.comanda], ['', p.forma_pagamento], ['', p.bandeira],
                ['Pago em', p.pago_em_data_posterior ? `${fmtDate(p.data_pagamento)} (posterior)` : ''], ['Caixa', p.caixa_aberto_por]],
            }))} />
          ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-[#E4E4E7]">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F6F4] text-[#71717A] text-xs">
                <tr>
                  <Th>Data</Th><Th>Comanda</Th><Th>Cliente</Th><Th right>Valor</Th>
                  <Th>Forma</Th><Th>Bandeira</Th><Th>Data pagto</Th><Th>Posterior</Th>
                  <Th>Caixa</Th>
                </tr>
              </thead>
              <tbody>
                {data.linhas.map((p, i) => (
                  <tr key={i} className="border-t border-[#F4F4F5]" data-testid={`row-entrada-${i}`}>
                    <Td>{fmtDate(p.data_comanda)}</Td>
                    <Td>{p.comanda}</Td>
                    <Td>{p.cliente_origem}</Td>
                    <Td right>{fmtBRL(p.valor_centavos)}</Td>
                    <Td>{p.forma_pagamento}</Td>
                    <Td>{p.bandeira}</Td>
                    <Td>{fmtDate(p.data_pagamento)}</Td>
                    <Td>{p.pago_em_data_posterior ? <span className="text-amber-700 text-[10px] font-semibold">SIM</span> : ''}</Td>
                    <Td>{p.caixa_aberto_por}</Td>
                  </tr>
                ))}
                {data.linhas.length === 0 && (<tr><td colSpan={9} className="px-4 py-6 text-center text-xs text-[#A1A1AA]">Sem linhas neste período.</td></tr>)}
              </tbody>
            </table>
          </div>
          )}
          <Footer>
            <div className="flex flex-wrap gap-4">
              <span>Linhas: <b>{data.qtd}</b></span>
              <span>Pagos posterior: <b>{data.pagamentos_pagos_em_data_posterior.qtd}</b> ({fmtBRL(data.pagamentos_pagos_em_data_posterior.valor_centavos)})</span>
            </div>
            <div className="ml-auto">Total: <b className="text-[#18181B]">{fmtBRL(data.total_centavos)}</b></div>
          </Footer>
          <div className="mt-3 bg-white rounded-xl border border-[#E4E4E7] p-3">
            <div className="text-xs font-semibold text-[#71717A] uppercase mb-2">Por forma de pagamento</div>
            <table className="w-full text-xs"><tbody>
              {data.por_forma_pagamento.map(f => (
                <tr key={f.forma_pagamento} className="border-b border-[#F4F4F5] last:border-b-0">
                  <td className="py-1 break-words">{f.forma_pagamento}</td>
                  <td className="py-1 text-right text-[#71717A] w-16">{f.qtd}×</td>
                  <td className="py-1 text-right font-medium w-28 whitespace-nowrap">{fmtBRL(f.valor_centavos)}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </>
      )}
    </Card>
  );
};

// ======================= Abas 2 e 3 (filtro por profissional) =======================
const slugNome = (s) => (s || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'sem_nome';
const PROF_VAZIO = '__vazio__';
const nomeProf = (p) => (!p || p === PROF_VAZIO ? '(sem profissional no Avec)' : p);
const valProf = (p) => p || PROF_VAZIO;
const fmtUnit = (c) => (c === null || c === undefined ? '—' : fmtBRL(c));

const useFiltroProf = (endpoint, inicio, fim) => {
  const [prof, setProf] = useState('');
  const base = inicio && fim ? `${API}/historico/producao/${endpoint}` : '';
  const qs = `?inicio=${inicio}&fim=${fim}${prof ? `&profissional=${encodeURIComponent(prof)}` : ''}`;
  const url = base ? `${base}${qs}` : '';
  const expUrl = base ? `${base}/export/excel${qs}` : '';
  const filename = `${endpoint}_${inicio}_a_${fim}${prof ? `_${slugNome(nomeProf(prof))}` : ''}.xlsx`;
  const { data, loading } = useReport(url, inicio, fim);
  useEffect(() => {
    if (prof && data && !data.profissionais.map(valProf).includes(prof)) setProf('');
  }, [data, prof]);
  return { prof, setProf, data, loading, expUrl, filename };
};

const FiltroProf = ({ prof, setProf, profissionais, testPrefix }) => (
  <div className="mb-3 flex items-center gap-2 flex-wrap">
    <label className="text-[10px] uppercase text-[#71717A] tracking-wide">Profissional</label>
    <select value={prof} onChange={(e) => setProf(e.target.value)} data-testid={`${testPrefix}-filtro-prof`} className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm">
      <option value="">Todos</option>
      {profissionais.map(p => <option key={valProf(p)} value={valProf(p)}>{nomeProf(p)}</option>)}
    </select>
    {prof && (
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs bg-[#ECFDF5] text-[#1B4332] border border-[#A7F3D0]" data-testid={`${testPrefix}-filtro-ativo`}>
        Filtrando: <b>{nomeProf(prof)}</b>
        <button onClick={() => setProf('')} data-testid={`${testPrefix}-ver-todos`} className="inline-flex items-center gap-1 underline font-medium">
          <X size={12} weight="bold" /> Ver todos
        </button>
      </span>
    )}
  </div>
);

const ResumoProfCards = ({ data, prof, setProf, testPrefix }) => (
  <div className="space-y-2">
    {data.resumo_profissionais.map((r, idx) => {
      const ativo = prof === valProf(r.profissional);
      return (
        <MobileRow key={valProf(r.profissional)} testId={`${testPrefix}-resumo-row-${idx}`} active={ativo}
          onClick={() => setProf(ativo ? '' : valProf(r.profissional))}
          title={nomeProf(r.profissional)} value={fmtBRL(r.total_centavos)} meta={[['Linhas', r.linhas], ['Qtd', r.qtd]]} />
      );
    })}
    <div className="px-3 py-2 rounded-xl border-2 border-[#E4E4E7]" data-testid={`${testPrefix}-resumo-total`}>
      <KV strong label={`Total geral · ${data.total_geral.linhas} linhas · qtd ${data.total_geral.qtd}`} value={fmtBRL(data.total_geral.total_centavos)} />
    </div>
  </div>
);

const ResumoProf = (props) => {
  const narrow = useNarrow();
  const { data, prof, setProf, testPrefix } = props;
  return (
  <div className="mb-3 bg-white rounded-xl border border-[#E4E4E7] p-3" data-testid={`${testPrefix}-resumo-prof`}>
    <div className="text-xs font-semibold text-[#71717A] uppercase mb-2">Resumo por profissional</div>
    {narrow ? <ResumoProfCards {...props} /> : (
    <table className="w-full text-xs">
      <thead className="text-[#A1A1AA] text-[10px] uppercase">
        <tr><th className="text-left py-1">Profissional</th><th className="text-right py-1 w-20">Linhas</th><th className="text-right py-1 w-20">Qtd</th><th className="text-right py-1 w-32">Total</th></tr>
      </thead>
      <tbody>
        {data.resumo_profissionais.map((r, idx) => {
          const ativo = prof === valProf(r.profissional);
          return (
            <tr key={valProf(r.profissional)} onClick={() => setProf(ativo ? '' : valProf(r.profissional))} data-testid={`${testPrefix}-resumo-row-${idx}`}
              className={`border-t border-[#F4F4F5] cursor-pointer transition-colors ${ativo ? 'bg-[#ECFDF5] font-semibold text-[#1B4332]' : 'hover:bg-[#F7F6F4]'}`}>
              <td className="py-1.5">{nomeProf(r.profissional)}</td>
              <td className="py-1.5 text-right">{r.linhas}</td>
              <td className="py-1.5 text-right">{r.qtd}</td>
              <td className="py-1.5 text-right">{fmtBRL(r.total_centavos)}</td>
            </tr>
          );
        })}
        <tr className="border-t-2 border-[#E4E4E7] font-semibold text-[#18181B]" data-testid={`${testPrefix}-resumo-total`}>
          <td className="py-1.5">Total geral</td>
          <td className="py-1.5 text-right">{data.total_geral.linhas}</td>
          <td className="py-1.5 text-right">{data.total_geral.qtd}</td>
          <td className="py-1.5 text-right">{fmtBRL(data.total_geral.total_centavos)}</td>
        </tr>
      </tbody>
    </table>
    )}
  </div>
  );
};

const RodapeFiltro = ({ data, prof, testPrefix }) => (
  <Footer>
    <span data-testid={`${testPrefix}-linhas`}>Linhas: <b>{data.qtd}</b></span>
    {prof && <span>Profissional: <b className="text-[#1B4332]">{nomeProf(prof)}</b></span>}
    <div className="ml-auto" data-testid={`${testPrefix}-total`}>Total: <b className="text-[#18181B]">{fmtBRL(data.total_centavos)}</b></div>
  </Footer>
);

const AbaServicos = ({ inicio, fim }) => {
  const { prof, setProf, data, loading, expUrl, filename } = useFiltroProf('servicos', inicio, fim);
  const narrow = useNarrow();
  return (
    <Card title="Serviços realizados" exportUrl={expUrl} filename={filename} testId="aba-servicos">
      {data && <FiltroProf prof={prof} setProf={setProf} profissionais={data.profissionais} testPrefix="serv" />}
      {loading && <Loading />}
      {!loading && data && (
        <>
          <ResumoProf data={data} prof={prof} setProf={setProf} testPrefix="serv" />
          {narrow ? (
            <MobileRows testPrefix="row-serv" empty="Sem serviços neste período." rows={data.linhas.map(i => ({
              title: i.descricao_origem, value: fmtBRL(i.total_centavos),
              meta: [['', fmtDate(i.data)], ['', i.dia_semana], ['Comanda', i.comanda], ['', nomeProf(i.profissional_origem)], ['', i.categoria_origem],
                ['Cliente', i.cliente_origem], ['Cel.', i.celular], ['Qtd', i.qtd], ['Unit.', fmtUnit(i.valor_unitario_centavos)],
                ['Desc.', i.desconto_centavos ? fmtBRL(i.desconto_centavos) : '']],
            }))} />
          ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-[#E4E4E7]">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F6F4] text-[#71717A] text-xs">
                <tr>
                  <Th>Profissional</Th><Th>Data</Th><Th>Dia</Th><Th>Comanda</Th>
                  <Th>Serviço</Th><Th>Categoria</Th><Th>Cliente</Th><Th>Celular</Th>
                  <Th right>Qtd</Th><Th right>Unit.</Th><Th right>Desc.</Th><Th right>Total</Th>
                </tr>
              </thead>
              <tbody>
                {data.linhas.map((i, idx) => (
                  <tr key={idx} className="border-t border-[#F4F4F5]" data-testid={`row-serv-${idx}`}>
                    <Td>{nomeProf(i.profissional_origem)}</Td><Td>{fmtDate(i.data)}</Td><Td>{i.dia_semana}</Td>
                    <Td>{i.comanda}</Td><Td>{i.descricao_origem}</Td><Td>{i.categoria_origem}</Td>
                    <Td>{i.cliente_origem}</Td><Td>{i.celular || ''}</Td>
                    <Td right>{i.qtd}</Td><Td right>{fmtUnit(i.valor_unitario_centavos)}</Td>
                    <Td right>{fmtBRL(i.desconto_centavos)}</Td><Td right>{fmtBRL(i.total_centavos)}</Td>
                  </tr>
                ))}
                {data.linhas.length === 0 && (<tr><td colSpan={12} className="px-4 py-6 text-center text-xs text-[#A1A1AA]">Sem serviços neste período.</td></tr>)}
              </tbody>
            </table>
          </div>
          )}
          <RodapeFiltro data={data} prof={prof} testPrefix="serv" />
        </>
      )}
    </Card>
  );
};

const AbaProdutos = ({ inicio, fim }) => {
  const { prof, setProf, data, loading, expUrl, filename } = useFiltroProf('produtos', inicio, fim);
  const narrow = useNarrow();
  return (
    <Card title="Produtos vendidos" exportUrl={expUrl} filename={filename} testId="aba-produtos">
      {data && <FiltroProf prof={prof} setProf={setProf} profissionais={data.profissionais} testPrefix="prod" />}
      {loading && <Loading />}
      {!loading && data && (
        <>
          <ResumoProf data={data} prof={prof} setProf={setProf} testPrefix="prod" />
          {narrow ? (
            <MobileRows testPrefix="row-prod" empty="Sem produtos neste período." rows={data.linhas.map(i => ({
              title: i.descricao_origem, value: fmtBRL(i.total_centavos),
              meta: [['', fmtDate(i.data)], ['Comanda', i.comanda], ['', nomeProf(i.profissional_origem)], ['Cliente', i.cliente_origem],
                ['Cód.', i.codigo_interno], ['', i.marca], ['', i.categoria_origem], ['Qtd', i.qtd],
                ['Custo', i.custo_origem_centavos ? fmtBRL(i.custo_origem_centavos) : '']],
            }))} />
          ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-[#E4E4E7]">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F6F4] text-[#71717A] text-xs">
                <tr>
                  <Th>Profissional</Th><Th>Data</Th><Th>Comanda</Th><Th>Cliente</Th>
                  <Th>Produto</Th><Th>Código</Th><Th>Marca</Th><Th>Categoria</Th>
                  <Th right>Qtd</Th><Th right>Valor</Th><Th right>Custo (origem)</Th>
                </tr>
              </thead>
              <tbody>
                {data.linhas.map((i, idx) => (
                  <tr key={idx} className="border-t border-[#F4F4F5]" data-testid={`row-prod-${idx}`}>
                    <Td>{nomeProf(i.profissional_origem)}</Td><Td>{fmtDate(i.data)}</Td><Td>{i.comanda}</Td>
                    <Td>{i.cliente_origem}</Td><Td>{i.descricao_origem}</Td><Td>{i.codigo_interno}</Td>
                    <Td>{i.marca}</Td><Td>{i.categoria_origem}</Td>
                    <Td right>{i.qtd}</Td><Td right>{fmtBRL(i.total_centavos)}</Td>
                    <Td right>{i.custo_origem_centavos ? fmtBRL(i.custo_origem_centavos) : ''}</Td>
                  </tr>
                ))}
                {data.linhas.length === 0 && (<tr><td colSpan={11} className="px-4 py-6 text-center text-xs text-[#A1A1AA]">Sem produtos neste período.</td></tr>)}
              </tbody>
            </table>
          </div>
          )}
          <RodapeFiltro data={data} prof={prof} testPrefix="prod" />
        </>
      )}
    </Card>
  );
};

// ======================= Aba 4 =======================
const AbaClientes = ({ inicio, fim }) => {
  const url = inicio && fim ? `${API}/historico/producao/clientes?inicio=${inicio}&fim=${fim}` : '';
  const expUrl = url.replace('/clientes?', '/clientes/export/excel?');
  const { data, loading } = useReport(url, inicio, fim);
  const narrow = useNarrow();
  return (
    <Card title="Profissionais e clientes atendidos" exportUrl={expUrl} filename={`clientes_${inicio}_a_${fim}.xlsx`} testId="aba-clientes">
      {loading && <Loading />}
      {!loading && data && (
        <>
          {narrow ? (
            <MobileRows testPrefix="row-cli" empty="Sem clientes neste período." rows={data.linhas.map(c => ({
              title: c.cliente_origem, value: fmtBRL(c.total_consumido_centavos),
              meta: [['Cel.', c.celular], ['Última visita', fmtDate(c.ultima_visita)], ['Visitas', c.numero_visitas], ['', c.profissionais.join(', ')]],
            }))} />
          ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-[#E4E4E7]">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F6F4] text-[#71717A] text-xs">
                <tr>
                  <Th>Cliente</Th><Th>Celular</Th><Th>Última visita</Th>
                  <Th right>Total consumido</Th><Th>Profissionais</Th><Th right>Visitas</Th>
                </tr>
              </thead>
              <tbody>
                {data.linhas.map((c, idx) => (
                  <tr key={idx} className="border-t border-[#F4F4F5]" data-testid={`row-cli-${idx}`}>
                    <Td>{c.cliente_origem}</Td><Td>{c.celular || ''}</Td>
                    <Td>{fmtDate(c.ultima_visita)}</Td>
                    <Td right>{fmtBRL(c.total_consumido_centavos)}</Td>
                    <Td>{c.profissionais.join(', ')}</Td>
                    <Td right>{c.numero_visitas}</Td>
                  </tr>
                ))}
                {data.linhas.length === 0 && (<tr><td colSpan={6} className="px-4 py-6 text-center text-xs text-[#A1A1AA]">Sem clientes neste período.</td></tr>)}
              </tbody>
            </table>
          </div>
          )}
          <Footer>
            <span>Clientes: <b>{data.qtd_clientes}</b></span>
            <div className="ml-auto">Total consumido: <b className="text-[#18181B]">{fmtBRL(data.total_centavos)}</b></div>
          </Footer>
        </>
      )}
    </Card>
  );
};

const Card = ({ title, children, exportUrl, filename, testId }) => (
  <section data-testid={testId}>
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <h3 className="text-base font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{title}</h3>
      {exportUrl && <ExportBtn url={exportUrl} filename={filename} testId={`${testId}-export`} />}
    </div>
    {children}
  </section>
);

const Th = ({ children, right }) => (
  <th className={`${right ? 'text-right' : 'text-left'} px-3 py-2 font-semibold uppercase tracking-wide`}>{children}</th>
);
const Td = ({ children, right }) => (
  <td className={`${right ? 'text-right' : 'text-left'} px-3 py-2`}>{children}</td>
);
const Footer = ({ children }) => (
  <div className="mt-2 px-3 py-2 bg-white rounded-xl border border-[#E4E4E7] text-xs text-[#71717A] flex items-center gap-4 flex-wrap">
    {children}
  </div>
);
const Loading = () => (
  <div className="text-center py-10 text-[#71717A]"><Spinner size={24} className="inline animate-spin mr-2" /> Carregando...</div>
);

export default ProducaoPage;
export { ProducaoPage };
